import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Text,
  Flex,
  Icon,
  Wrap,
  IconButton,
  VStack,
  Link,
} from "@chakra-ui/react";
import { useDropzone, FileWithPath } from "react-dropzone";
import { IoMdClose } from "react-icons/io";
import { AiTwotoneFile } from "react-icons/ai";
import { MdOutlineFileUpload } from "react-icons/md";
import { KeywordDialogBox } from "./KeywordDialogBox";

// Inline worker code as a string for Blob worker
const workerCode = `
  self.onmessage = function(e) {
    const { fileContent } = e.data;
    if (!fileContent) {
      self.postMessage({ keywords: [] });
      return;
    }
    const keywords = fileContent
      .split('\\n')
      .map((keyword) => keyword.toLowerCase().replace(/"/g, '').trim())
      .filter((keyword) => keyword !== '');
    self.postMessage({ keywords });
  };
`;

function createWorker() {
  const blob = new Blob([workerCode], { type: "application/javascript" });
  return new Worker(URL.createObjectURL(blob));
}

export interface FileUploadProps {
  acceptedFileTypes: string;
  name: string;
  maxFiles?: number;
  setFiles: (name: string, files: FileWithPath[]) => void;
}

export const FileUpload = ({
  name,
  setFiles,
  acceptedFileTypes,
  maxFiles,
}: FileUploadProps) => {
  // Avoid TS "union type too complex" from deeply generic Chakra `Flex` in this tree.
  const FlexAny = Flex as any;
  const IconButtonAny = IconButton as any;
  const WrapAny = Wrap as any;
  const VStackAny = VStack as any;
  const TextAny = Text as any;
  const LinkAny = Link as any;

  const [uploadedFiles, setUploadedFiles] = useState<FileWithPath[]>([]);

  const [inputMounted, setInputMounted] = useState<boolean>(true);
  maxFiles = maxFiles ?? 4;

  const [formattedFileContent, setFormattedFileContent] = useState<string[]>(
    []
  );
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [loadingAnalogBox, setLoadingAnalogBox] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!inputMounted) {
      setInputMounted(true);
    }
  }, [inputMounted]);

  useEffect(() => {
    if (uploadedFiles.length > 0) {
      const file = uploadedFiles[0];
      setOpenDialog(true);
      setLoadingAnalogBox(true);

      const reader = new FileReader();
      reader.onload = function (event) {
        const fileContent = event.target?.result;
        if (!fileContent) return;
        setError("");
        const worker = createWorker();
        worker.postMessage({ fileContent: fileContent.toString() });
        worker.onmessage = (e) => {
          const keywords = e.data.keywords;
          if (keywords.length > 20000) {
            setError(
              `The uploaded file exceeds the maximum limit of 20,000. Please remove the file and reduce the number of keywords and try again.`
            );
            setFormattedFileContent([]);
            setLoadingAnalogBox(false);
            removeFile(file)();
            worker.terminate();
            return;
          }
          setFormattedFileContent(e.data.keywords);
          setLoadingAnalogBox(false);
          worker.terminate();
        };
      };
      reader.readAsText(file);
    }
  }, [uploadedFiles]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      const newFiles = [...acceptedFiles, ...uploadedFiles];
      // Loop through to check for duplicates. We first sort the files by name
      // then we filter all the files with duplicate names out.
      const newFilesUnique = newFiles
        .sort((a, b) => {
          if (a.name < b.name) {
            return -1;
          } else if (a.name > b.name) {
            return 1;
          } else {
            return 0;
          }
        })
        .filter(
          (current, index, array) =>
            index === 0 || current.name !== array[index - 1].name
        )
        .slice(0, maxFiles);

      setUploadedFiles(newFilesUnique);
      setFiles(name, newFilesUnique);
    },
    [uploadedFiles]
  );

  const removeFile = (file: FileWithPath) => () => {
    const newFiles = uploadedFiles.concat();
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
    setFiles(name, newFiles);
    setInputMounted(false);
  };

  const MAX_FILE_SIZE = 1048576; // 1MB in bytes
  const { getRootProps, getInputProps, isDragAccept, isDragReject } =
    useDropzone({
      onDrop,
      accept: acceptedFileTypes,
      maxFiles: maxFiles,
      maxSize: MAX_FILE_SIZE,
    });

  const getBgColor = () => {
    if (isDragAccept) {
      return "green.200";
    } else if (isDragReject) {
      return "error";
    } else {
      return "primary.lighter";
    }
  };

  // const getOtherColor = () => {
  //   if (isDragAccept) {
  //     return "green.600";
  //   } else if (isDragReject) {
  //     return "red.600";
  //   } else {
  //     return "primary.dark";
  //   }
  // };

  return (
    <>
      <KeywordDialogBox
        keywords={formattedFileContent}
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        loading={loadingAnalogBox}
      />
      <Box
        py={10}
        px={4}
        borderRadius={"3px"}
        borderWidth={"1px"}
        borderColor={"greyInput"}
        _hover={{ cursor: "pointer" }}
      >
        <Box {...getRootProps({ style: { width: "100%", height: "100%" } })}>
          {inputMounted && (
            <Box>
              <input {...getInputProps()} />
              <WrapAny spacing={2} align={"center"} justify={"center"}>
                <VStackAny>
                  <Box
                    width={"40px"}
                    height={"40px"}
                    borderRadius={"full"}
                    backgroundColor={getBgColor()}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderColor={"#F5FCFD"}
                    borderWidth={"3px"}
                  >
                    {<Icon as={MdOutlineFileUpload} width={5} height={5} />}
                  </Box>

                  <TextAny
                    fontWeight={"medium"}
                    fontSize={12}
                    color={"formLabelText"}
                  >
                    <LinkAny
                      color={"#57becc"}
                      fontSize={12}
                      textDecoration={"none"}
                    >
                      Click to upload
                    </LinkAny>

                    {" or drag and drop"}
                  </TextAny>
                  <TextAny fontSize={11} color={"formLabelText"}>
                    {"Import only .txt (max 1MB)"}
                  </TextAny>
                </VStackAny>
              </WrapAny>
            </Box>
          )}
          {error && (
            <Box mt={2} p={3} backgroundColor="#FDEDEC" borderRadius="3px">
              <TextAny fontSize={12} color="#D81111">
                {error}
              </TextAny>
            </Box>
          )}
        </Box>
      </Box>
      {!!uploadedFiles.length && (
        <Box mt={2} backgroundColor={"#F6F6F6"}>
          {uploadedFiles.map((file: FileWithPath, ix) => (
            <FlexAny
              key={`${file.path}-${ix}`}
              justifyContent={"space-between"}
            >
              <FlexAny align={"center"}>
                <Box
                  width={"35px"}
                  height={"35px"}
                  borderRadius={"full"}
                  backgroundColor={getBgColor()}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderColor={"#F5FCFD"}
                  borderWidth={"3px"}
                  ml={2}
                  mr={2}
                >
                  <AiTwotoneFile color="var(--chakra-colors-primary-dark, #1a365d)" />
                </Box>
                <span style={{ fontWeight: 500 }}>{file.path}</span>
              </FlexAny>
              <IconButtonAny
                bg={"transparent"}
                _hover={{ bg: "primary.light" }}
                aria-label={"Remove file"}
                onClick={removeFile(file)}
                icon={<IoMdClose />}
              />
            </FlexAny>
          ))}
        </Box>
      )}
    </>
  );
};
